import { useRef } from 'react'
import Image from 'next/image'
import { useSlider } from '@react-aria/slider'
import { useSliderState } from '@react-stately/slider'
import { AriaSliderProps } from '@react-types/slider'
import { useNumberFormatter } from '@react-aria/i18n'
import { formatCurrency2CompactString } from 'src/utils/number/currency'
import { Thumb } from './components/Thumb'
import { Marks } from './components/Marks'
import styles from './styles.module.scss'

export type SliderProps = AriaSliderProps & {
  className?: string
  formatOptions?: {
    useGrouping?: boolean
  }
  marks?: boolean
  onReset?: () => void
  value: number[]
}

export const Slider = (props: SliderProps) => {
  const { value, marks, label, minValue, maxValue, onReset } = props

  const trackRef = useRef(null)
  const numberFormatter = useNumberFormatter(props.formatOptions)
  const state = useSliderState({ ...props, numberFormatter })
  const { groupProps, trackProps, labelProps, outputProps } = useSlider(
    props,
    state,
    trackRef
  )

  const isRange = value.length > 1

  const dynamicWith =
    value && isRange
      ? `${
          100 -
          state.getThumbPercent(0) * 100 -
          (100 - state.getThumbPercent(1) * 100)
        }%`
      : `${state.getThumbPercent(0) * 100}%`

  const dynamicLeft =
    value && isRange ? `${state.getThumbPercent(0) * 100}%` : 0

  return (
    <div {...groupProps} className={styles.slider}>
      <div className={styles.labelContainer}>
        {label && <label {...labelProps}>{label}</label>}
        <output className={styles.output} {...outputProps}>
          {formatCurrency2CompactString(state.getThumbValue(0))}
          {isRange &&
            ` - ${formatCurrency2CompactString(state.getThumbValue(1))}`}
        </output>
        <div className={styles.resetButton} onClick={onReset}>
          <Image
            src='/assets/icons/icon-reset-filter.svg'
            width={20}
            height={20}
            alt='Reset'
          />
        </div>
      </div>
      <div className={styles.trackContainer} {...trackProps} ref={trackRef}>
        <div className={styles.trackDisabled} />
        {marks && (
          <Marks
            minValue={minValue}
            onChangeValue={state.setThumbValue}
            maxValue={maxValue}
            values={state.values}
            isRange={isRange}
          />
        )}

        <div
          className={styles.track}
          style={{
            width: dynamicWith,
            left: dynamicLeft,
          }}
        />

        {value.map((val, key) => (
          <Thumb key={key} index={key} state={state} trackRef={trackRef} />
        ))}
      </div>
      <div className={styles.valuesContainer}>
        <span>{formatCurrency2CompactString(minValue)}</span>
        <span>{formatCurrency2CompactString(maxValue)}</span>
      </div>
    </div>
  )
}
